import React, { useMemo, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { Tree, MultiBackend, getBackendOptions, DndProvider } from "@minoru/react-dnd-treeview";
import { selectFlow, setEditorSaved } from "../../../../redux/actions/integrationActions";
import { getActionIcon } from "./ActionIcon";

const ROOT_ID = "ROOT";

const normalizeParentId = (value) => {
  if (typeof value !== "string") {
    return "";
  }
  return value;
};

const ActionTree = ({ callback, activeClassName = "" }) => {
  const flow = useSelector((state) => state.integration.flow);
  const saved = useSelector((state) => state.integration.saved);
  const dispatch = useDispatch();
  const { sectionId = "" } = useParams();
  const treeRef = useRef(null);

  const actionsById = useMemo(() => {
    if (!Array.isArray(flow?.actions)) {
      return {};
    }

    return flow.actions.reduce((acc, action) => {
      if (action?.id) {
        acc[action.id] = action;
      }
      return acc;
    }, {});
  }, [flow?.actions]);

  const sanitizedActions = useMemo(() => {
    if (!Array.isArray(flow?.actions)) {
      return [];
    }

    const actionIds = new Set(flow.actions.map((action) => action?.id).filter((id) => typeof id === "string" && id.length > 0));

    return flow.actions
      .map((action) => {
        if (!action?.id) {
          return null;
        }

        const parentId = normalizeParentId(action.parentId);
        const hasValidParent = parentId.length > 0 && parentId !== action.id && actionIds.has(parentId);

        return {
          ...action,
          parentId: hasValidParent ? parentId : "",
        };
      })
      .filter(Boolean);
  }, [flow?.actions]);

  const treeData = useMemo(() => {
    return sanitizedActions
      .slice()
      .sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER))
      .map((action) => ({
        id: action.id,
        parent: action.parentId ? action.parentId : ROOT_ID,
        text: action.name || "Untitled action",
        droppable: true,
        data: action,
      }));
  }, [sanitizedActions]);

  const canDropOnNode = useCallback((tree, { dragSourceId, dropTargetId }) => {
    if (!dragSourceId || !dropTargetId || dropTargetId === ROOT_ID) {
      return true;
    }

    if (dragSourceId === dropTargetId) {
      return false;
    }

    let currentParent = dropTargetId;
    while (currentParent && currentParent !== ROOT_ID) {
      if (currentParent === dragSourceId) {
        return false;
      }

      const parentNode = tree.find((node) => node.id === currentParent);
      if (!parentNode) {
        break;
      }
      currentParent = parentNode.parent;
    }

    return true;
  }, []);

  const ensureNodeIsOpen = useCallback(
    (nodeId) => {
      if (nodeId && nodeId !== ROOT_ID) {
        treeRef.current?.open(nodeId);
      }
    },
    []
  );

  const handleDrop = useCallback(
    (newTree, { dropTargetId } = {}) => {
      ensureNodeIsOpen(dropTargetId);

      if (!Array.isArray(flow?.actions)) {
        return;
      }

      const updatedActions = [];
      let hasChanges = false;

      newTree.forEach((node, index) => {
        const originalAction = actionsById[node.id];
        if (!originalAction) {
          return;
        }

        const nextParentId = node.parent === ROOT_ID ? "" : node.parent;
        const normalizedParentId = normalizeParentId(originalAction.parentId);
        const nextOrder = (index + 1) * 100;
        const previousOrder = typeof originalAction.order === "number" ? originalAction.order : null;

        if (normalizedParentId !== nextParentId || previousOrder !== nextOrder) {
          hasChanges = true;
        }

        updatedActions.push({
          ...originalAction,
          parentId: nextParentId,
          order: nextOrder,
        });
      });

      const seenIds = new Set(updatedActions.map((action) => action.id));
      flow.actions.forEach((action) => {
        if (!action?.id) {
          updatedActions.push(action);
          return;
        }

        if (!seenIds.has(action.id)) {
          updatedActions.push(action);
        }
      });

      if (!hasChanges) {
        return;
      }

      const updatedFlow = {
        ...flow,
        actions: updatedActions,
      };

      dispatch(selectFlow(updatedFlow));
      dispatch(setEditorSaved(false));
    },
    [actionsById, dispatch, ensureNodeIsOpen, flow]
  );

  const renderActionTypeBadge = (action) => {
    if (action.type === "WEB_REQUEST_ACTION_TYPE") {
      return <span className={"ihub-action-menu-list-method " + (action.method || "")}>{action.method || "REQ"}</span>;
    }
    if (action.type === "SCRIPT_ACTION_TYPE") {
      return <span className="ihub-action-menu-list-method scripted">{"</>"}</span>;
    }
    return null;
  };

  const renderBehaviourIcons = (action) => {
    const icons = [];

    if (action?.iterator) {
      icons.push(getActionIcon("ITERATION"));
    }

    if (action?.pagination?.enabled) {
      icons.push(getActionIcon("PAGINATION"));
    }

    if (action?.conditions && action.conditions.length > 0) {
      icons.push(getActionIcon("CONDITION"));
    }

    return icons.filter(Boolean).map((icon, index) => (
      <span key={index}>
        {icon}
      </span>
    ));
  };

  const renderNode = useCallback(
    (node, { depth, isOpen, hasChild, containerRef, handleRef, isDragging, onToggle }) => {
      const action = node?.data;
      if (!action) {
        return null;
      }

      const isActive = sectionId === node.id;
      const actionNameClass = "ihub-action-list-action-name" + (action.enabled === false ? " action-disabled" : "");
      const displayName = action.name || "Untitled action";

      const rowClasses = [
        "ihub-action-tree-row",
        depth > 0 && !hasChild ? "ihub-action-tree-row-guided" : "",
        isDragging ? "dragging" : "",
      ]
        .filter(Boolean)
        .join(" ");

      return (
        <div className={rowClasses} ref={containerRef} style={{ marginLeft: depth * 12, "--tree-depth": depth }}>
          <div className="ihub-action-tree-label" ref={handleRef}>
            <div className="ihub-action-tree-toggle-wrapper">
              {hasChild ? (
                <button
                  type="button"
                  className="ihub-action-tree-toggle"
                  onClick={(event) => {
                    event.stopPropagation();
                    onToggle();
                  }}
                >
                  {isOpen ? "-" : "+"}
                </button>
              ) : (
                <span className={`ihub-action-tree-toggle-placeholder ${depth === 0 ? "root-bullet" : ""}`} />
              )}
            </div>
            <div className="ihub-action-list-entry">
              <a onClick={() => callback && callback(node.id)} className={`ihub-action-list-style ${isActive ? activeClassName : ""}`}>
                {renderActionTypeBadge(action)}
                {isActive && !saved ? (
                  <>
                    *<span className={actionNameClass}>{displayName}</span>
                  </>
                ) : (
                  <span className={actionNameClass}>{displayName}</span>
                )}
              </a>
              <div className="ihub-action-list-action-icons">{renderBehaviourIcons(action)}</div>
            </div>
          </div>
        </div>
      );
    },
    [activeClassName, callback, saved, sectionId]
  );

  if (!Array.isArray(flow?.actions) || flow.actions.length === 0) {
    return <div className="ihub-action-tree-empty">No actions yet</div>;
  }

  return (
    <DndProvider backend={MultiBackend} options={getBackendOptions()}>
      <Tree
        ref={treeRef}
        tree={treeData}
        rootId={ROOT_ID}
        render={renderNode}
        initialOpen={true}
        canDrop={canDropOnNode}
        onDrop={handleDrop}
        listComponent="div"
        listItemComponent="div"
        classes={{ root: "ihub-action-tree-root" }}
        sort={false}
        insertDroppableFirst={false}
      />
    </DndProvider>
  );
};

export default ActionTree;
